import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ConfirmPopup } from 'primereact/confirmpopup';
import _ from 'lodash';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import Breadcrumb from '../../components/Breadcrumb';
import Icon from '../../icons/Icon';
import CustomTabv3, { CustomTabItemProps } from 'components/custom/CustomTabv3';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { dispatch, RootState, store } from '../../redux/store';
import { initialPagePath, PageState } from '../../redux/reducers/PageReducer';
import { DefaultFilter, UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import CollectionNames from '../../constant/CollectionNames';
import { CaseSettings } from '../../domain/case/CaseDomains';
import { changePageFilter, clearPage } from '../../redux/actions/PageActions';
import { changeCaseColumns, changeContainerLoading } from '../../redux/actions/CaseActions';
import { setAttribute } from '../../redux/actions/UserActions';
import { CaseState } from '../../redux/reducers/CaseReducer';
import CustomSkeleton from '../../components/custom/CustomSkeleton';
import ItemToolsMenu from '../../components/ItemToolsMenu';
import EntityListComponent from './EntityListComponent';
import { TransactionJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import ComponentName from '../../constant/ComponentName';
import FilterUtil from '../../util/FilterUtil';
import DefaultFilterUtil from '../../util/DefaultFilterUtil';
import ValueFactory from '../../util/ValueFactory';
import { CustomFilterFields } from '../../domain/ui/FilterDomains';
import FolderService from '../../service/FolderService';
import IntervalManager from '../../util/IntervalManager';
import NavigationUtil from '../../util/NavigationUtil';
import QueryBuilder = query.QueryBuilder;
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import eq = query.eq;
import LanguageUtil from '../../util/LanguageUtil';

class EntityContainer extends Component<Props, State> {
  public state: State = new State();

  private chatSubscription: subscribe.Subscription | undefined;
  private caseSettingsSubscription: subscribe.Subscription | undefined;
  private channelSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly intervalManager: IntervalManager = InstanceContainer.get(Injectables.INTERVAL_MANAGER);

  public render() {
    const { selectedTab, loading, filter, settings, channel, updates } = this.state;
    return (
      <div className='w-full h-full flex flex-col overflow-y-hidden'>
        <Breadcrumb />
        <ConfirmPopup />
        <div className={'flex w-full shrink-0 px-5 pt-5 gap-x-5 overflow-x-hidden'}>
          <div className='w-[80px] shrink-0 h-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE]'>
            <Icon icon={'entity'} size={30} className={'hidden'} color={'#728EFF'} />
          </div>
          <div className={'flex flex-col gap-y-1 overflow-x-scroll'}>
            <span className={'text-4xl text-black font-normal'}>{LanguageUtil.get({ id: 'common.label.entityPage' })}</span>
            <CustomTabv3 tab={this.getTabItems()} onClick={(e: any, tab: string) => this.onChangeDomain(tab)} selectedTab={selectedTab} />
          </div>
        </div>
        <div className={'flex justify-between bg-white items-center w-full px-5'}>
          <ItemToolsMenu
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            importVisible={false}
            component={ComponentName.ENTITY}
            componentId={selectedTab}
          />
        </div>
        <div className={'px-5 h-full'}>
          {!loading ? (
            <EntityListComponent
              selectedTab={selectedTab}
              key={updates[selectedTab ?? '']}
              channel={channel}
              caseSettings={settings}
              filter={filter}
              onFetchCount={(value: number) => this.setState({ totalRecord: value })}
              onChangeFilter={this.onChangeFilter}
              onTablePropChange={(sort: Order[], rows: number, currentPage: number) => {
                this.setState({ sort, rows, currentPage });
              }}
            />
          ) : (
            <CustomSkeleton />
          )}
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location },
    } = this.props;
    const page = store.getState().page;
    const initial = initialPagePath[ComponentName.ENTITY];
    this.intervalManager.clearEntityIntervals();
    if (page.pagePath.length == 0 || page.pagePath[0].title != initial?.title) {
      await FolderService.calculate('WITHOUT_FOLDER', ComponentName.ENTITY, location.pathname, store.getState().page);
    }
    await this.resubscribeTracker();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevState.channel != this.state.channel) {
      this.incrementCurrent();
    }
  }

  public componentWillUnmount = async () => {
    this.intervalManager.clearEntityIntervals();
    this.props.clearPage();
    this.props.changeCaseColumns({});
    await this.stopTracker();
  };

  private startTracker = () => {
    const caseSettingsQuery = QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).select('name', 'dataSource', 'entities').build();
    this.caseSettingsSubscription = subscribe.Formica.subscribe(CollectionNames.CASE_SETTINGS, caseSettingsQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.caseSettingsSubscription]);
  };

  private stopTracker = async () => {
    this.caseSettingsSubscription?.stop();
    this.channelSubscription?.stop();
    this.channelSubscription = undefined;
    this.chatSubscription?.stop();
    this.chatSubscription = undefined;
    this.computation?.stop();
  };

  private tracker = async () => {
    const { setting } = this.state;
    if (this.caseSettingsSubscription?.ready()) {
      await this.buildData();
      if (setting) {
        const channel: TransactionJsonModel | undefined = await this.repository.find(
          QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', setting.dataSource)).build(),
        );
        this.setState({ channel });
      }
    }
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
      return;
    }
    this.computation?.onStop(() => {
      this.startTracker();
    });
    await this.stopTracker();
  };

  private trySubscribeToChannel = (settings: CaseSettings[]) => {
    const { selectedTab } = this.state;
    if (settings == undefined || this.channelSubscription != undefined) return;
    const setting: CaseSettings | undefined = settings.find((config: CaseSettings) => config?.entities?.some((c) => c.name == selectedTab));
    this.setState({ setting });
    if (setting == undefined) return;
    this.channelSubscription = subscribe.Formica.subscribe(
      CollectionNames.TRANSACTIONS,
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', setting.dataSource)).build(),
    );
    this.computation?.addSubscription(this.channelSubscription);
  };

  private subscribeToChats = () => {
    const { selectedTab } = this.state;
    if (this.chatSubscription != undefined) return;
    const chatQuery = query.QueryBuilder.select()
      .where(eq('domain', selectedTab))
      .limit(200)
      .orderBy({
        field: 'timestamp',
        operator: 'ASC',
      })
      .build();
    this.chatSubscription = subscribe.Formica.subscribe(`entity_chat_log_${NavigationUtil.getTenant()}`, chatQuery, undefined, 'elasticsearch');
  };

  private buildData = async () => {
    const { selectedTab } = this.state;
    const {
      router: { params },
    } = this.props;
    const allSettings = await this.repository.findMany<CaseSettings>(QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build());
    if (selectedTab == undefined && params.domain == undefined) {
      const sortedSettings = allSettings.map((setting: any) => {
        const sortedEntities = setting?.entities?.sort((a: any, b: any) => {
          if (a.name && b.name) {
            return a.name.localeCompare(b.name);
          }
          return 0;
        });
        return {
          ...setting,
          entities: sortedEntities,
        };
      });
      const setting = sortedSettings.find((setting: any) => setting?.entities?.[0]?.name != undefined);
      const tab = (setting as CaseSettings)?.entities?.[0]?.name;
      this.setState({ selectedTab: tab }, () => this.onChangeDomain(tab));
    }
    if (selectedTab == undefined && params.domain != undefined) {
      this.setState({ selectedTab: params.domain }, () => this.onChangeDomain(params.domain));
    }
    this.trySubscribeToChannel(allSettings);
    this.subscribeToChats();
    this.setState({ settings: allSettings, loading: false }, () => {
      this.props.changeContainerLoading(false);
      this.getFilterFields(true);
    });
  };

  private getFilterFields = async (saveFilter: boolean = false) => {
    const { selectedTab, setting } = this.state;
    const { changePageFilter, attributes, user } = this.props;
    const { page } = store.getState();
    const defaultFilter: DefaultFilter | undefined = DefaultFilterUtil.find(attributes.filters, {
      component: ComponentName.ENTITY,
      id: selectedTab,
      riskType: user!.activeRiskType,
    });
    if (defaultFilter) {
      const newFilter = defaultFilter.filter.filter.fields.filter(
        (field: any) => !page.filter.filter.fields.some((pageField: any) => pageField.field == field.field),
      );
      if (newFilter.length > 0) {
        page.filter.filter.fields.push(...newFilter);
        changePageFilter({
          filter: page.filter.filter,
          items: [],
        });
      }
    }
    const variables = setting?.entities.find((e) => e?.name === selectedTab)?.variables || [];
    return FilterUtil.getEntityFields(setting?.dataSource, variables || [], saveFilter);
  };

  private prepareFilterFields = async (domain: string) => {
    const { changePageFilter, attributes, user } = this.props;
    let filter = ValueFactory.initializeFilter();
    if (domain) {
      const defaultFilter: DefaultFilter | undefined = _.cloneDeep(
        DefaultFilterUtil.find(attributes.filters, {
          component: ComponentName.ENTITY,
          id: domain,
          riskType: user!.activeRiskType,
        }),
      );
      if (defaultFilter) {
        filter = defaultFilter.filter.filter;
      }
    }
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ENTITY);
    changePageFilter({
      filter: filter,
      items: fields,
    });
    this.setState({ filter: FilterUtil.parse(filter), filterLoading: false });
  };

  private onChangeDomain = async (domain: string) => {
    const { updates } = this.state;
    const {
      router: { navigate },
    } = this.props;
    this.setState({ loading: true, filterLoading: true }, async () => {
      this.props.changeContainerLoading(true);
      navigate(`/entity/${domain}`, { replace: true });
      await this.prepareFilterFields(domain);
      dispatch(setSelectedItems([]));
      updates[domain] = (updates[domain] ?? 0) + 1;
      this.setState({ updates, selectedTab: domain }, this.resubscribeTracker);
    });
  };

  private incrementCurrent = () => {
    const { updates, selectedTab } = this.state;
    updates[selectedTab ?? '__all__'] = (updates[selectedTab ?? '__all__'] ?? 0) + 1;
    this.setState({ updates });
  };

  private getTabItems = (): CustomTabItemProps[] => {
    const { settings } = this.state;
    const items: CustomTabItemProps[] = [];
    for (const setting of settings) {
      const entities = (setting as CaseSettings)?.entities;
      if (entities && entities.length > 0) {
        const sortedEntities = entities.sort((a, b) => {
          if (a.name && b.name) {
            return a.name.localeCompare(b.name);
          }
          return 0;
        });
        for (const entity of sortedEntities) {
          const name = entity?.name;
          if (name != undefined) {
            const label: ReactNode = <span className='flex gap-[4px]'>{name}</span>;
            items.push({
              key: name,
              label: label,
            });
          }
        }
      }
    }
    return items;
  };

  private onChangeFilter = (filter: Filter) => {
    if (_.isEqual(filter, this.state.filter)) return;
    this.setState({ filter }, this.incrementCurrent);
  };
}

interface Props extends FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  page: state.page as PageState,
  attributes: state.auth.attributes as UserSystemAttributes,
  component: state.component as ComponentState,
  case: state.case as CaseState,
});
const connector = connect(mapStateToProps, { clearPage, changePageFilter, changeCaseColumns, setAttribute, changeContainerLoading });
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  selectedTab: string | undefined;
  settings: CaseSettings[] = [];
  setting: CaseSettings | undefined;
  channel: TransactionJsonModel | undefined;
  loading: boolean = true;
  totalRecord: number = 0;
  reason: string | undefined;
  filter: Filter = empty();
  filterLoading: boolean = true;
  sort: Order[] = [];
  rows: number = 10;
  currentPage: number = 1;
  updates: Record<string, number> = {};
}

export default connector(withFormica(EntityContainer));
